import { SessionInfo } from 'shared/models/session';
import { HeaderKeys, MimeType } from 'modules/common/requests/Definitions';
import { RequestExecutor } from 'modules/common/requests/RequestExecutor';

// need to create dedicated executor here that doesn't use executor factory in order to avoid a dependency loop of
// RegionalRequestExecutor.ts -> session.ts -> ExecutorFactory.ts -> RegionalRequestExeuctor.ts.
// This particularly makes tests fail, like the RegionalRequestExecutor tests because they have trouble creating subclasses of
// classes that haven't yet been defined
const DevsiteApiExecutor = new RequestExecutor<string>('/api', {
  defaultHeaders: { [HeaderKeys.CONTENT_TYPE]: MimeType.JSON },
});

/**
 * ClientSession manages all information and processing of a developer site session
 * for client use. It caches all session info an automatically listens for updates to a session
 * from other tabs.
 */
class ClientSession {
  // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
  private session: SessionInfo = null;
  private static readonly SESSION_UPDATE_KEY = 'sessionUpdated';
  private readonly sessionListeners: ((sessionInfo: SessionInfo) => void)[] = [];

  constructor() {
    this.listenForSessionUpdate();
    // we shouldn't auto fetch on tests
    if (process.env.NODE_ENV !== 'test') {
      this.refresh();
    }
  }

  /**
   * Fetch the current session info. This will return info from the cache if available.
   * @returns the current session info
   */
  getInfo = async () => {
    if (this.session) return this.session;

    return this.refresh();
  };

  getCsrfToken = async () => {
    const session = await this.getInfo();

    return session.csrfToken;
  };

  isSignedIn = () => this.session?.exp && Date.now() / 1000 < this.session.exp;

  /**
   * Force a session cache clear.
   */
  clearCache = () => {
    // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
    this.session = null;
  };

  /**
   * Refresh the client session with a call to the devsite server. Also notifies all listeners.
   * @returns the new session info
   */
  private refresh = async () => {
    const resp = await DevsiteApiExecutor.get('/v1/sessionInfo');
    this.session = resp.body;
    this.notifySessionChangeListeners();
    return this.session;
  };

  /**
   * Allow consumers to listen into session updates
   * @param listener
   * @returns the listener id
   */
  addSessionChangeListener = (listener: (session: SessionInfo) => void): number => this.sessionListeners.push(listener);

  /**
   * Stop listening to session changes.
   * @param listenerId received when first adding the listener
   */
  removeSessionChangeListener = (listenerId: number) => {
    // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
    this.sessionListeners[listenerId] = undefined;
  };

  /**
   * Loops through all valid listeners and notifies them with the current session
   */
  private notifySessionChangeListeners = () => {
    this.sessionListeners.filter((listener) => listener).forEach((listener) => listener(this.session));
  };

  /**
   * Notifies the app and all tabs of a new Developer Site user session.
   */
  notifyTabsOfNewSession = async () => {
    // our notification is simply the current date time, as we do not want to actually
    // expose session information inside local storage. Since each app keeps their session
    // info in memory, they'll just refresh their cache. See 'listenForSessionUpdate' for details.
    localStorage.setItem(ClientSession.SESSION_UPDATE_KEY, Date.now().toString());
    return this.refresh();
  };

  /**
   * Listens for any session updates in other tabs. When other tabs complete login flows,
   * they will attempt to notify all other tabs via session storage events. When notified,
   * update the current client session in memory and notify the app (all the listeners).
   *
   * This will keep tabs from becoming stale in the session cache if a user logs in to one tab
   * after their token expires. It ensures all tabs will have the latest session info no matter
   * which tab logs in.
   */
  private listenForSessionUpdate = () => {
    window.addEventListener('storage', (event) => {
      if (event.key === ClientSession.SESSION_UPDATE_KEY) {
        this.refresh();
      }
    });
  };
}

// Instance of a session manager to get info about the client session. If inside a component,
// you can most likely just utilize the session context.
export const session = new ClientSession();
